import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  UseOfForceAuthorizationModel,
  UseOfForceSummaryRegistrationModel,
  UseOfForceSummaryRegistrationStatus,
} from '../../../../api-service/clients';
import { CardComponent } from '../../../../components/card-component';
import { ColorPalette } from '../../../../components/config';
import { WindowService } from '../../../../components/window-service/window-service';
import { formatDate } from '../../../../utilities';
import { ServiceProviderRoute, ServiceProviderUrlBuilder } from '../utils/service-provider-url-builder';
import {
  createUseOfForceSummaryRegistration,
  getUseOfForceAuthorizationsAtUnit,
  getUseOfForceSummaryRegistrations,
  registerUseOfForceAuthorizationAtUnit,
  revokeUseOfForceAuthorizationAtUnit,
} from './use-of-force-api';
import { UseOfForceAuthorizationRegistrationDialog } from './use-of-force-authorization-registration-dialog';
import { UseOfForceAuthorizationsTable } from './use-of-force-authorizations-table';

interface UseOfForceAuthorizationsCardProps {
  citizenId: string;
  serviceProviderUnitId: string;
  reloadCount: number;
}

// Everyone at the unit sees the afgørelser they act under; the leader registers those from another kommune (decisions 63, 69).
export function UseOfForceAuthorizationsCard(
  props: UseOfForceAuthorizationsCardProps
) {
  const { citizenId, serviceProviderUnitId, reloadCount } = props;
  const [authorizations, setAuthorizations] =
    useState<UseOfForceAuthorizationModel[]>();
  const [summaryRegistrations, setSummaryRegistrations] = useState<
    UseOfForceSummaryRegistrationModel[]
  >([]);
  const [canAdminister, setCanAdminister] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [creatingSummaryFor, setCreatingSummaryFor] = useState<string>();

  useEffect(() => {
    let isCurrent = true;
    async function load() {
      const [result, summaries] = await Promise.all([
        getUseOfForceAuthorizationsAtUnit(citizenId, serviceProviderUnitId),
        getUseOfForceSummaryRegistrations(citizenId, serviceProviderUnitId),
      ]);
      if (!isCurrent) return;
      setAuthorizations(result.authorizations);
      setCanAdminister(result.canAdminister);
      setCanRegister(result.canRegister);
      setSummaryRegistrations(summaries.registrations);
    }
    load();
    return () => {
      isCurrent = false;
    };
  }, [citizenId, serviceProviderUnitId, reloadCount]);

  if (!authorizations) return null;

  function replace(changed: UseOfForceAuthorizationModel) {
    setAuthorizations(current =>
      current?.map(authorization =>
        authorization.id === changed.id ? changed : authorization
      )
    );
  }

  function openSummaryRegistration(registrationId: string) {
    WindowService.open(
      ServiceProviderUrlBuilder.build(
        ServiceProviderRoute.USE_OF_FORCE_SUMMARY_REGISTRATION,
        { citizenId, spuId: serviceProviderUnitId, summaryRegistrationId: registrationId }
      )
    );
  }

  async function createSummaryRegistration(authorizationId: string) {
    setCreatingSummaryFor(authorizationId);
    try {
      const created = await createUseOfForceSummaryRegistration(
        citizenId,
        serviceProviderUnitId,
        authorizationId
      );
      setSummaryRegistrations(current => [created, ...current]);
      openSummaryRegistration(created.id);
    } finally {
      setCreatingSummaryFor(undefined);
    }
  }

  // Skema 2 is the leader's (decision 73); one draft at a time per afgørelse.
  function summaryActions(authorization: UseOfForceAuthorizationModel) {
    if (!canAdminister) return null;
    const registrations = summaryRegistrations.filter(
      registration => registration.authorizationId === authorization.id
    );
    const hasDraft = registrations.some(
      registration =>
        registration.status === UseOfForceSummaryRegistrationStatus.Draft
    );

    return (
      <>
        {authorization.awaitsSummaryRegistration && (
          <Typography variant="caption" color="warning.main" sx={{ mr: 1 }}>
            Skema 2 mangler
          </Typography>
        )}
        {registrations.map(registration => (
          <Button
            key={registration.id}
            size="small"
            onClick={() => openSummaryRegistration(registration.id)}
          >
            {registration.status === UseOfForceSummaryRegistrationStatus.Draft
              ? 'Skema 2 (kladde)'
              : `Skema 2 (${formatDate(registration.approvedAt)})`}
          </Button>
        ))}
        {!hasDraft && (
          <Button
            size="small"
            disabled={creatingSummaryFor === authorization.id}
            onClick={() => createSummaryRegistration(authorization.id)}
          >
            Udfyld Skema 2
          </Button>
        )}
      </>
    );
  }

  return (
    <CardComponent
      title="Forhåndsgodkendelser"
      contentBackgroundColor={ColorPalette.WHITE}
    >
      <UseOfForceAuthorizationsTable
        authorizations={authorizations}
        canRevoke={authorization =>
          canAdminister && authorization.registeredByServiceProvider
        }
        onRevoke={async authorization =>
          replace(
            await revokeUseOfForceAuthorizationAtUnit(
              citizenId,
              serviceProviderUnitId,
              authorization.id
            )
          )
        }
        renderActions={summaryActions}
      />
      {canRegister && (
        <Box sx={{ mt: 2 }}>
          <Button size="small" onClick={() => setIsRegistering(true)}>
            Registrér eksisterende afgørelse
          </Button>
        </Box>
      )}
      {isRegistering && (
        <UseOfForceAuthorizationRegistrationDialog
          onSave={async registration => {
            const registered = await registerUseOfForceAuthorizationAtUnit(
              citizenId,
              serviceProviderUnitId,
              registration
            );
            setAuthorizations(current => [registered, ...(current ?? [])]);
            setIsRegistering(false);
          }}
          onClose={() => setIsRegistering(false)}
        />
      )}
    </CardComponent>
  );
}
